import type React from "react";
import { useCallback, useMemo, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { chosenSites } from "app/common/services/chosenSites";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { useHDBannerCta } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";
import { ChannelAnalysisChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChart";
import DurationService from "app/services/DurationService";

import { mapTabToMetricAndType } from "../../../components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { DATE_FORMATTER, DEFAULT_CHANNEL_TRAFFIC_SOURCE, filterBetaMmxChannels } from "../../consts";
import type { AddToDashboardButtonProps } from "../../types/types";

import useChannelTrafficChartConfig from "./hooks/useBetaChannelTrafficConfig";
import useBetaChannelTrafficData from "./hooks/useBetaChannelTrafficData";
import { getLastWindowDate, handleChannelFilterChange, handleMetricChange, isPeriodOverPeriod } from "./utils";

interface BetaChannelTrafficChartContainerProps {
    addToDashboardButtonProps: AddToDashboardButtonProps;
}
const BetaChannelTrafficChartContainer: React.FC<BetaChannelTrafficChartContainerProps> = ({
    addToDashboardButtonProps,
}) => {
    const wrapperRef = useRef<HTMLInputElement>(null);
    const { params } = useCurrentStateAndParams();
    const {
        key,
        channelAnalysisChannel = DEFAULT_CHANNEL_TRAFFIC_SOURCE,
        duration,
        comparedDuration,
        webSource,
    } = params;
    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );
    const {
        switcherViewModeConfig,
        switcherGranularitiesConfig,
        mtdOnOffConfig,
        isShowMMXAlertBell,
        selectedVisualizationIndex,
        setSelectedVisualizationIndex,
        selectedGranularity,
        isMTDActive,
        startDate,
        endDate,
        subtitleFilters,
    } = useChannelTrafficChartConfig(durationObject);

    const { data, isLoading, getExcelUrl } = useBetaChannelTrafficData({
        durationObject,
        selectedGranularity,
        isMTDActive,
    });
    const activeChannels = params?.activeChannels?.split(",");
    /* The chart renders the shared tooltip, so the banner it carries needs its CTA wired here too */
    const hdBannerCtaState = useHDBannerCta({ trackingName: "Graph tooltip/channel traffic" });
    const hdBannerCtaStateRef = useRef(hdBannerCtaState);
    hdBannerCtaStateRef.current = hdBannerCtaState;
    const getHdBannerCtaState = useCallback(() => hdBannerCtaStateRef.current, []);
    const graphComponentRef = useRef<ChannelAnalysisChart>(null);
    const keys = useMemo(() => chosenSites.sitelistForLegend(), [key]);

    const getAddToDashboard = useCallback(
        ({ metric, selectedChannel }) => {
            return (
                <AddToDashboard
                    webSource={webSource}
                    type={mapTabToMetricAndType[metric].type}
                    metric={mapTabToMetricAndType[metric].metric}
                    modelType="fromWebsite"
                    filters={{
                        timeGranularity: selectedGranularity.value,
                    }}
                    overrideAddToDashboardParams={{
                        ...(!!selectedChannel && { selectedChannel }),
                    }}
                    buttonProps={addToDashboardButtonProps}
                />
            );
        },
        [webSource, addToDashboardButtonProps],
    );
    return (
        <div
            ref={wrapperRef}
            data-automation="betaChannelsTrafficChartContainer"
        >
            <WidgetContainer
                title={translate("wa.mmx.channel_analysis.new.title")}
                tooltip={translate("wa.mmx.allTraffic.channel_analysis.title.tooltip")}
                isLoading={isLoading}
                loaderWidth={1320}
                loaderHeight={300}
                subtitleFilters={subtitleFilters}
                switcherConfig={switcherGranularitiesConfig}
                switcherViewMode={switcherViewModeConfig}
                onOffSwitchConfig={mtdOnOffConfig}
                noData={!data}
            >
                <ChannelAnalysisChart
                    ref={graphComponentRef}
                    isLoading={isLoading}
                    startDate={startDate.format(DATE_FORMATTER)}
                    endDate={endDate.format(DATE_FORMATTER)}
                    mtdStartDate={endDate.clone().add(1, "days").startOf("day").format(DATE_FORMATTER)}
                    mtdEndDate={getLastWindowDate().format(DATE_FORMATTER)}
                    isMTDOn={isMTDActive}
                    selectedGranularity={selectedGranularity.value}
                    data={data}
                    isPoP={isPeriodOverPeriod()}
                    getExcelUrl={getExcelUrl}
                    keys={keys}
                    webSource={webSource}
                    durationObject={durationObject}
                    addToDashboard={getAddToDashboard}
                    selectedChannelFilter={channelAnalysisChannel}
                    handleChannelFilterChange={handleChannelFilterChange}
                    selectedMetric="TrafficShare"
                    handleMetricChange={handleMetricChange}
                    domain={key}
                    selectedVisualizationIndex={selectedVisualizationIndex}
                    setSelectedVisualizationIndex={setSelectedVisualizationIndex}
                    isShowMMXAlertBell={isShowMMXAlertBell()}
                    activeChannels={activeChannels}
                    filterChannels={filterBetaMmxChannels}
                    chartColors={CHART_COLORS.trafficSourcesColorsBySourceMMX}
                    trafficChannelsCount={filterBetaMmxChannels.length}
                    getHdBannerCtaState={getHdBannerCtaState}
                />
            </WidgetContainer>
        </div>
    );
};

export default BetaChannelTrafficChartContainer;
